import * as api from "./client";

// ============================================================================
// Type Definitions
// ============================================================================

export type Translation = {
  termId: string;
  translatedName: string;
};

export type TranslationsQueryRequest = {
  termIds: string[];
};

export type TranslationsQueryResponse = {
  translations: Translation[];
  languageCode: string;
  requestId?: string;
};

// ============================================================================
// Region to Language Code Mapping
// ============================================================================

const REGION_TO_LANGUAGE_CODE: Record<string, string> = {
  Argentina: "es-es",
  Bolivia: "es-es",
  Brazil: "pt-pt",
  "Central America": "es-es",
  Chile: "es-es",
  China: "zh-cn",
  Colombia: "es-es",
  "Costa Rica": "es-es",
  Deutschland: "de-de",
  "Dominican Republic": "es-es",
  Ecuador: "es-es",
  "El Salvador": "es-es",
  France: "fr-fr",
  Germany: "de-de",
  Guatemala: "es-es",
  Honduras: "es-es",
  "Hong Kong": "zh-cn",
  "Hong Kong Bar": "zh-cn",
  Italy: "it-it",
  "Latin America: International firms": "es-es",
  Mexico: "es-es",
  Nicaragua: "es-es",
  Panama: "es-es",
  Paraguay: "es-es",
  Peru: "es-es",
  Portugal: "pt-pt",
  "Puerto Rico": "es-es",
  Spain: "es-es",
  Uruguay: "es-es",
  Venezuela: "es-es",
};

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get language code for a given region name
 */
export function getLanguageCodeForRegion(regionName: string | undefined): string | null {
  if (!regionName) return null;
  return REGION_TO_LANGUAGE_CODE[regionName] || null;
}

// ============================================================================
// Service
// ============================================================================

export const translationsService = {
  /**
   * Query translations for a list of term IDs in a specific language
   */
  async query(languageCode: string, termIds: string[]): Promise<TranslationsQueryResponse> {
    const endpoint = `/v1/taxonomy/editorial-categories/translations/${languageCode}/query`;
    const requestBody: TranslationsQueryRequest = { termIds };

    return api.post<TranslationsQueryResponse>(endpoint, requestBody);
  },

  /**
   * Batch query translations for multiple term IDs, handling empty arrays
   * Automatically splits requests into batches of 1000 to respect API limits
   */
  async batchQuery(languageCode: string, termIds: string[]): Promise<Map<string, string>> {
    if (termIds.length === 0 || !languageCode) {
      return new Map();
    }

    const BATCH_SIZE = 1000;
    const translationMap = new Map<string, string>();

    // Process in batches to respect API limit of 1000 term IDs per request
    for (let i = 0; i < termIds.length; i += BATCH_SIZE) {
      const batch = termIds.slice(i, i + BATCH_SIZE);
      const response = await this.query(languageCode, batch);

      response.translations.forEach(translation => {
        translationMap.set(translation.termId, translation.translatedName);
      });
    }

    return translationMap;
  },
};
